import { Injectable } from '@angular/core';

import { OrganizationMembershipStatus } from '../api';
import { type Organization } from '../organizations/organizations.abstraction';
import { RouteFragment } from '../shared/common';
import { UserProfile } from '../shared/services/user/user.abstraction';

import {
  type OrganizationPageAccessMapHandler,
  type OrganizationRouteFragments,
} from './routing.abstraction';

const DEFAULT_ORGANIZATION_ROUTE_FRAGMENTS: readonly OrganizationRouteFragments[] = [
  [RouteFragment.StrategyBoard],
  [RouteFragment.FinanceManagement],
  [RouteFragment.DataAnalytics],
  [RouteFragment.OperationsServices, RouteFragment.Overview],
  [RouteFragment.CompanyDocuments],
];

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly organizationPageAccessMap = new Map<string, OrganizationPageAccessMapHandler>([
    [
      RouteFragment.StrategyBoard,
      ({ capabilities }) => capabilities.portal.strategyBoard,
    ],
    [
      RouteFragment.FinanceManagement,
      ({ capabilities }) => capabilities.portal.financeManagement,
    ],
    [
      RouteFragment.DataAnalytics,
      ({ capabilities }) => capabilities.portal.dataAnalytics,
    ],
    [
      RouteFragment.CompanyDocuments,
      ({ capabilities }) => capabilities.portal.dataUploader,
    ],
    [
      RouteFragment.OperationsServices,
      (organization, childRouteFragment) =>
        this.canAccessOperationsPage(organization, childRouteFragment),
    ],
    [
      RouteFragment.Roles,
      ({ capabilities }) => capabilities.portal.organizationRoles,
    ],
  ]);

  private readonly operationsSubPagesAccessMap = new Map<string, OrganizationPageAccessMapHandler>([
    [
      RouteFragment.Overview,
      (organization) => this.hasAnyOperationsAccess(organization),
    ],
    [
      RouteFragment.Legal,
      ({ capabilities }) => capabilities.portal.operationsLegal,
    ],
    [
      RouteFragment.Finance,
      ({ capabilities }) => capabilities.portal.operationsFinance,
    ],
    [
      RouteFragment.Hr,
      ({ capabilities }) => capabilities.portal.operationsHr,
    ],
  ]);

  canAccessAdminPanel(profile: UserProfile | null | undefined): boolean {
    return Boolean(profile?.permissions.canViewAdminPanel);
  }

  canAccessOrganizationPage(
    organization: Organization | null | undefined,
    routeFragments: OrganizationRouteFragments,
  ): boolean {
    if (!organization) {
      return false;
    }

    const [routeFragment, childRouteFragment] = routeFragments;
    const accessHandler = this.organizationPageAccessMap.get(routeFragment);

    return accessHandler?.(organization, childRouteFragment) ?? false;
  }

  canManageOrganizationRoles(organization: Organization | null | undefined): boolean {
    if (!organization) {
      return false;
    }

    const canGrantRoles = organization.capabilities.portal.grantRoles;
    const isActiveMember = organization.membership?.status === OrganizationMembershipStatus.Active;

    return canGrantRoles && isActiveMember;
  }

  getDefaultOrganizationRouteFragments(
    organization: Organization | null | undefined,
  ): string[] {
    return [...(DEFAULT_ORGANIZATION_ROUTE_FRAGMENTS.find((routeFragments) =>
      this.canAccessOrganizationPage(organization, routeFragments),
    ) ?? [])];
  }

  getGrantableOrganizationRoles(
    organization: Organization | null | undefined,
  ): Organization['capabilities']['grantableRoles'] {
    return organization?.capabilities.grantableRoles ?? [];
  }

  hasAnyOperationsAccess(organization: Organization | null | undefined): boolean {
    const portal = organization?.capabilities.portal;

    return portal?.operationsLegal === true
      || portal?.operationsFinance === true
      || portal?.operationsHr === true;
  }

  private canAccessOperationsPage(
    organization: Organization,
    childRouteFragment: string = RouteFragment.Overview,
  ): boolean {
    const accessHandler = this.operationsSubPagesAccessMap.get(childRouteFragment);

    return accessHandler?.(organization) ?? false;
  }
}
